import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner, Form } from 'react-bootstrap';
import { FaTrophy, FaPlus, FaBuilding, FaEdit, FaTrash, FaEye, FaUniversity, FaTshirt, FaUsers, FaUserPlus, FaExclamationTriangle, FaGift, FaFileExcel, FaCalendarAlt, FaUserSecret, FaExchangeAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import organizacionService from '../../services/organizacionService';
import './TournamentManagement.css';
import torneoService from '../../services/torneoService';
import temporadaService from '../../services/temporadaService';
import sedeService from '../../services/sedeService';
import postgresUserService from '../../services/postgresUserService';
import * as XLSX from 'xlsx';
import api from '../../services/api';

/**
 * Gestión de Torneos y Partidos para Administradores
 * 
 * Esta página permite a los administradores:
 * - Ver su organización asociada
 * - Gestionar torneos (CRUD completo)
 * - Gestionar calendarios y jornadas
 * - Crear nuevos torneos
 */
const TournamentManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Estados principales
  const [torneos, setTorneos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados para modales
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTorneo, setSelectedTorneo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estado para organización, temporadas y sedes
  const [organizacion, setOrganizacion] = useState(null);
  const [temporadas, setTemporadas] = useState([]);
  const [sedesDisponibles, setSedesDisponibles] = useState([]);
  const [sedesSeleccionadas, setSedesSeleccionadas] = useState([]);
  const [sedesDelTorneo, setSedesDelTorneo] = useState([]); // Para edición

  // Estados para modal de calendario
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [calendarTorneo, setCalendarTorneo] = useState(null);
  const [calendarSedes, setCalendarSedes] = useState([]);
  const [calendarSedeActiva, setCalendarSedeActiva] = useState(null);
  const [calendarJornadas, setCalendarJornadas] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [newJornada, setNewJornada] = useState({ fecha: '', hora: '' });

  // Estados para modal de creación
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    nombre: '',
    fecha_inicio: '',
    fecha_fin: '',
    jornadas: '',
    temporada: '',
    tipo: 'normal'
  });

  // Estados para formulario de edición
  const [editForm, setEditForm] = useState({
    nombre: '',
    fecha_inicio: '',
    fecha_fin: '',
    jornadas: ''
  });

  // Cargar datos iniciales
  useEffect(() => {
    if (user && user.id) {
      loadData();
    }
  }, [user]);

  // Función principal para cargar datos
  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Cargando datos para admin:', user.id);
      
      // 1. Buscar el usuario en PostgreSQL para obtener su ID
      const usuariosPostgresRes = await postgresUserService.getUsuarios();
      const usuariosPostgres = usuariosPostgresRes.data || [];

      // Buscar el usuario admin actual por email
      let usuarioPostgresEncontrado = usuariosPostgres.find(u =>
        u.email === user.email
      );

      // Si no se encuentra, intentar sincronizar desde MongoDB
      if (!usuarioPostgresEncontrado) {
        console.log('🔄 Usuario no encontrado en PostgreSQL, intentando sincronizar...');

        try {
          const syncResponse = await fetch('/api/postgres/sync/admin-to-postgres', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('prokids_token')}`
            },
            body: JSON.stringify({ email: user.email })
          });

          if (syncResponse.ok) {
            const syncResult = await syncResponse.json();
            console.log('✅ Usuario sincronizado:', syncResult);

            // Buscar nuevamente después de la sincronización
            const usuariosPostgresRes2 = await postgresUserService.getUsuarios();
            const usuariosPostgres2 = usuariosPostgresRes2.data || [];
            usuarioPostgresEncontrado = usuariosPostgres2.find(u => u.email === user.email);
          }
        } catch (syncError) {
          console.error('❌ Error en sincronización:', syncError);
        }
      }

      if (!usuarioPostgresEncontrado) {
        throw new Error('Usuario administrador no encontrado en PostgreSQL. Contacte al administrador del sistema.');
      }
      
      console.log('👤 Usuario PostgreSQL encontrado:', usuarioPostgresEncontrado);
      
      // 2. Buscar la organización asociada al gestor
      const organizacionRes = await organizacionService.getOrganizacionPorGestor(usuarioPostgresEncontrado.id);
      
      if (!organizacionRes.success) {
        throw new Error('No se encontró organización asociada a este administrador');
      }

      console.log('🏢 Organización encontrada:', organizacionRes.data);
      setOrganizacion(organizacionRes.data);

      // Cargar temporadas y sedes
      const [temporadasRes, sedesRes] = await Promise.all([
        temporadaService.getTemporadas(),
        sedeService.getSedes()
      ]);
      if (temporadasRes.success) {
        setTemporadas(temporadasRes.data);
      }
      if (sedesRes.success) {
        setSedesDisponibles(sedesRes.data);
      }

      // 3. Cargar torneos de la organización
      const torneosRes = await torneoService.getTorneosPorOrganizacion(organizacionRes.data.id);
      
      if (torneosRes.success) {
        console.log('🏆 Torneos cargados:', torneosRes.data);

        // Debug: verificar fechas de torneos
        torneosRes.data.forEach(torneo => {
          const fechaFin = new Date(torneo.fecha_fin);
          const hoy = new Date();
          const esActivo = fechaFin >= hoy;
          console.log(`📊 ${torneo.nombre}: ${torneo.fecha_fin} -> ${esActivo ? 'ACTIVO' : 'FINALIZADO'}`);
        });

        setTorneos(torneosRes.data);
        
        // 4. Datos cargados correctamente
      }
      
    } catch (err) {
      console.error('❌ Error cargando datos:', err);
      setError(err.message || 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  // Función para abrir modal de creación
  const handleCreateTorneo = () => {
    setCreateForm({
      nombre: '',
      fecha_inicio: '',
      fecha_fin: '',
      jornadas: '',
      temporada: temporadas.find(t => t.activa)?.id || '',
      tipo: 'normal'
    });
    setSedesSeleccionadas([]);
    setShowCreateModal(true);
  };

  // Función para manejar cambios en el formulario de creación
  const handleCreateFormChange = (e) => {
    const { name, value } = e.target;
    setCreateForm(prev => ({ ...prev, [name]: value }));
  };

  // Añadir/quitar sede en selección de creación
  const handleToggleSede = (sede) => {
    setSedesSeleccionadas(prev => {
      const existe = prev.find(s => s.id === sede.id);
      if (existe) return prev.filter(s => s.id !== sede.id);
      return [...prev, { ...sede, equipacion_nueva: false }];
    });
  };

  const handleToggleEquipacionNueva = (e, sedeId) => {
    e.stopPropagation();
    setSedesSeleccionadas(prev => prev.map(s => s.id === sedeId ? { ...s, equipacion_nueva: !s.equipacion_nueva } : s));
  };

  // Función para guardar nuevo torneo
  const handleSaveCreateTorneo = async (e) => {
    e.preventDefault();
    if (!organizacion) return;

    setIsSubmitting(true);
    try {
      const result = await torneoService.createTorneo({
        ...createForm,
        organizacion_id: organizacion.id,
        jornadas: parseInt(createForm.jornadas),
        temporada: parseInt(createForm.temporada)
      });
      if (result.success) {
        // Asociar sedes seleccionadas al torneo recién creado
        const torneoId = result.data.id;
        for (const sede of sedesSeleccionadas) {
          await torneoService.addSedeToTorneo(torneoId, sede.id, sede.nombre, sede.equipacion_nueva);
        }
        loadData();
        setShowCreateModal(false);
        setSedesSeleccionadas([]);
        setCreateForm({ nombre: '', fecha_inicio: '', fecha_fin: '', jornadas: '', temporada: '', tipo: 'normal' });
      } else {
        setError(result.error || 'Error al crear torneo');
      }
    } catch (err) {
      setError(err.message || 'Error al crear torneo');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Función para editar torneo
  const handleEditTorneo = async (torneo) => {
    setSelectedTorneo(torneo);
    setEditForm({
      nombre: torneo.nombre || '',
      fecha_inicio: torneo.fecha_inicio || '',
      fecha_fin: torneo.fecha_fin || '',
      jornadas: torneo.jornadas || ''
    });
    // Cargar sedes asociadas al torneo
    try {
      const sedesRes = await sedeService.getSedesPorTorneo(torneo.id);
      if (sedesRes.success) {
        setSedesDelTorneo(sedesRes.data || []);
      } else {
        setSedesDelTorneo([]);
      }
    } catch {
      setSedesDelTorneo([]);
    }
    setShowEditModal(true);
  };

  // Añadir sede al torneo (en edición)
  const handleAddSedeToTorneo = async (sede) => {
    if (!selectedTorneo) return;
    const result = await torneoService.addSedeToTorneo(selectedTorneo.id, sede.id, sede.nombre);
    if (result.success) {
      // Normalizar al formato que usa getSedesDelTorneo
      const nuevaSede = {
        sede_en_torneo_id: result.data.id,
        sede_id: sede.id,
        sede_nombre: sede.nombre,
        sede_poblacion: sede.poblacion || null,
        equipacion_nueva: false
      };
      setSedesDelTorneo(prev => [...prev, nuevaSede]);
    } else {
      setError(result.error || 'Error al añadir sede');
    }
  };

  // Quitar sede del torneo (en edición)
  const handleRemoveSedeFromTorneo = async (sedeEnTorneo) => {
    if (!selectedTorneo) return;
    const result = await torneoService.removeSedeFromTorneo(selectedTorneo.id, sedeEnTorneo.id);
    if (result.success) {
      setSedesDelTorneo(prev => prev.filter(s => s.sede_en_torneo_id !== sedeEnTorneo.id));
    } else {
      setError(result.error || 'Error al quitar sede');
    }
  };

  const handleToggleEquipacionNuevaEdit = async (e, sedeEnTorneoId, currentValue) => {
    e.stopPropagation();
    if (!selectedTorneo) return;
    const newValue = !currentValue;
    const result = await torneoService.updateSedeEnTorneo(selectedTorneo.id, sedeEnTorneoId, { equipacion_nueva: newValue });
    if (result.success) {
      setSedesDelTorneo(prev => prev.map(s => s.sede_en_torneo_id === sedeEnTorneoId ? { ...s, equipacion_nueva: newValue } : s));
    } else {
      setError(result.error || 'Error al actualizar equipación nueva de sede');
    }
  };

  // === FUNCIONES CALENDARIO ===
  const handleOpenCalendar = async (torneo) => {
    setCalendarTorneo(torneo);
    setCalendarLoading(true);
    setShowCalendarModal(true);
    try {
      const sedesRes = await sedeService.getSedesPorTorneo(torneo.id);
      const sedes = sedesRes.success ? (sedesRes.data || []) : [];
      setCalendarSedes(sedes);
      if (sedes.length > 0) {
        setCalendarSedeActiva(sedes[0]);
        await loadJornadasSede(torneo.id, sedes[0].sede_en_torneo_id);
      }
    } catch { setCalendarSedes([]); }
    setCalendarLoading(false);
  };

  const loadJornadasSede = async (torneoId, sedeEnTorneoId) => {
    const res = await torneoService.getJornadasDeSede(torneoId, sedeEnTorneoId);
    setCalendarJornadas(res.success ? res.data : []);
  };

  const handleSelectSedeTab = async (sede) => {
    setCalendarSedeActiva(sede);
    setCalendarLoading(true);
    await loadJornadasSede(calendarTorneo.id, sede.sede_en_torneo_id);
    setCalendarLoading(false);
  };

  const handleAddJornada = async () => {
    if (!calendarTorneo || !calendarSedeActiva || !newJornada.fecha) return;
    const siguienteNumero = calendarJornadas.length > 0
      ? Math.max(...calendarJornadas.map(j => j.numero)) + 1 : 1;
    const result = await torneoService.createJornada(
      calendarTorneo.id, calendarSedeActiva.sede_en_torneo_id,
      { numero: siguienteNumero, fecha: newJornada.fecha, hora: newJornada.hora || null }
    );
    if (result.success) {
      await loadJornadasSede(calendarTorneo.id, calendarSedeActiva.sede_en_torneo_id);
      setNewJornada({ fecha: '', hora: '' });
    } else {
      setError(result.error);
    }
  };

  const handleDeleteJornada = async (jornadaId) => {
    if (!calendarTorneo || !window.confirm('¿Eliminar esta jornada?')) return;
    const result = await torneoService.deleteJornada(calendarTorneo.id, jornadaId);
    if (result.success) {
      await loadJornadasSede(calendarTorneo.id, calendarSedeActiva.sede_en_torneo_id);
    } else {
      setError(result.error);
    }
  };

  const handleUpdateJornada = async (jornadaId, field, value) => {
    if (!calendarTorneo) return;
    const data = {};
    data[field] = value;
    const result = await torneoService.updateJornada(calendarTorneo.id, jornadaId, data);
    if (result.success) {
      await loadJornadasSede(calendarTorneo.id, calendarSedeActiva.sede_en_torneo_id);
    } else {
      setError(result.error);
    }
  };

  // Función para manejar cambios en el formulario de edición
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Función para guardar cambios del torneo
  const handleSaveEditTorneo = async (e) => {
    e.preventDefault();
    if (!selectedTorneo) return;

    setIsSubmitting(true);
    try {
      const result = await torneoService.updateTorneo(selectedTorneo.id, editForm);
      if (result.success) {
        // Recargar datos
        loadData();
        // Cerrar modal y limpiar estado
        setShowEditModal(false);
        setSelectedTorneo(null);
        setEditForm({
          nombre: '',
          fecha_inicio: '',
          fecha_fin: '',
          jornadas: ''
        });
      } else {
        setError(result.error || 'Error al actualizar torneo');
      }
    } catch (err) {
      setError(err.message || 'Error al actualizar torneo');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Función para eliminar torneo
  const handleDeleteTorneo = (torneo) => {
    setSelectedTorneo(torneo);
    setShowDeleteModal(true);
  };

  // Función para navegar al panel de transferencias
  const handleGoToTransfers = () => {
    navigate('/admin/transfers');
  };

  // Función para exportar Excel de Seguimiento de Futbolistas
  const handleExportExcelSeguimiento = async (torneo) => {
    try {
      setLoading(true);
      console.log('📊 Exportando Excel de Seguimiento para torneo:', torneo.id);

      // Llamar al endpoint del backend
      const response = await api.get(`/reports/excel-seguimiento/${torneo.id}`);

      if (!response.data.success) {
        throw new Error('Error al obtener datos del servidor');
      }

      const futbolistas = response.data.data || [];

      if (futbolistas.length === 0) {
        alert('No hay futbolistas inscritos en este torneo');
        return;
      }

      // Preparar datos para Excel
      const excelData = futbolistas.map(f => ({
        'ID Futbolista': f.futbolista_id,
        'Nombre': f.futbolista_nombre,
        'Fecha Nacimiento': f.fecha_nacimiento ? new Date(f.fecha_nacimiento).toLocaleDateString('es-ES') : 'N/A',
        'Teléfono Tutor': f.tutor_phone,
        'Estado': f.estado,
        'Jornadas Participadas': f.jornadas_participadas,
        'Total Jornadas Jugadas': f.total_jornadas_jugadas,
        'Jornadas Futuras Inscritas': f.jornadas_futuras_inscritas,
        'Saldo Jornadas': f.saldo_jornadas
      }));

      // Crear el libro de Excel
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Seguimiento');

      // Configurar ancho de columnas
      const columnWidths = [
        { wch: 15 }, // ID Futbolista
        { wch: 25 }, // Nombre
        { wch: 18 }, // Fecha Nacimiento
        { wch: 15 }, // Teléfono Tutor
        { wch: 15 }, // Estado
        { wch: 30 }, // Jornadas Participadas
        { wch: 20 }, // Total Jornadas Jugadas
        { wch: 25 }, // Jornadas Futuras Inscritas
        { wch: 15 }  // Saldo Jornadas
      ];
      worksheet['!cols'] = columnWidths;

      // Generar nombre del archivo
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `seguimiento_futbolistas_${torneo.nombre.replace(/\s+/g, '_')}_${timestamp}.xlsx`;

      // Descargar archivo
      XLSX.writeFile(workbook, filename);

      console.log(`✅ Excel exportado: ${filename}`);
      alert(`Excel exportado correctamente: ${futbolistas.length} futbolistas`);

    } catch (error) {
      console.error('❌ Error al exportar Excel:', error);
      alert('Error al exportar Excel de Seguimiento');
    } finally {
      setLoading(false);
    }
  };

  // Función para confirmar eliminación
  const confirmDeleteTorneo = async () => {
    if (!selectedTorneo) return;

    setIsSubmitting(true);
    try {
      const result = await torneoService.deleteTorneo(selectedTorneo.id);
      if (result.success) {
        // Recargar datos
        loadData();
        // Cerrar modal y limpiar estado
        setShowDeleteModal(false);
        setSelectedTorneo(null);
      } else {
        setError(result.error || 'Error al eliminar torneo');
      }
    } catch (err) {
      setError(err.message || 'Error al eliminar torneo');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Función para ver detalles del torneo
  const handleViewTorneo = (torneo) => {
    setSelectedTorneo(torneo);
    setShowViewModal(true);
  };

  // Función para ver futbolistas inscritos
  const handleViewFutbolistas = (torneo) => {
    navigate(`/admin/tournaments/${torneo.id}/players`, {
      state: {
        torneo: torneo,
        returnPath: '/admin/matches'
      }
    });
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'No especificada';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Renderizar loading
  if (loading) {
    return (
      <AdminLayout>
        <Container fluid className="py-4">
          <div className="text-center">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Cargando...</span>
            </Spinner>
            <p className="mt-3">Cargando información de torneos...</p>
          </div>
        </Container>
      </AdminLayout>
    );
  }

  // Renderizar error
  if (error) {
    return (
      <AdminLayout>
        <Container fluid className="py-4">
          <Alert variant="danger">
            <Alert.Heading>Error</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-danger" onClick={loadData}>
              Reintentar
            </Button>
          </Alert>
        </Container>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div className="page-header mb-4 bg-white border-bottom py-3">
        <Container fluid>
          <Row>
            <Col>
              <div className="d-flex align-items-center">
                <FaBuilding className="text-primary me-3" size={32} />
                <div>
                  <h4 className="mb-1 text-primary">AP8Soccer</h4>
                  <p className="text-muted mb-0 small">
                    <strong>Gestor:</strong> Admin User (admin@prokidsapp.com)
                  </p>
                  <p className="text-muted mb-0 small">
                    <strong>Contacto:</strong> info@ap8soccer.com
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container fluid className="py-4">

        {/* Sección de Torneos */}
        <Row>
          <Col>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <FaTrophy className="me-2 text-warning" />
                    Torneos de AP8Soccer
                  </h5>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-success"
                      onClick={handleGoToTransfers}
                      className="d-flex align-items-center"
                    >
                      <FaUniversity className="me-2" />
                      Transferencias
                    </Button>
                    <Button
                      variant="outline-success"
                      onClick={() => navigate('/admin/scouts')}
                      className="d-flex align-items-center"
                    >
                      <FaUserSecret className="me-2" />
                      Scouts
                    </Button>
                    <Button
                      variant="outline-success"
                      onClick={() => navigate('/admin/promos')}
                      className="d-flex align-items-center"
                    >
                      <FaGift className="me-2" />
                      Promos
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleCreateTorneo}
                      className="d-flex align-items-center"
                    >
                      <FaPlus className="me-2" />
                      Crear Nuevo Torneo
                    </Button>
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                {torneos.length === 0 ? (
                  <div className="text-center py-5">
                    <FaTrophy className="text-muted mb-3" size={48} />
                    <h5 className="text-muted">No hay torneos creados</h5>
                    <p className="text-muted">Crea tu primer torneo para comenzar a gestionar partidos y jornadas.</p>
                  </div>
                ) : (
                  <div className="row">
                      {torneos.map((torneo) => {
                        const hoy = new Date();
                        const fechaInicio = new Date(torneo.fecha_inicio);
                        const fechaFin = new Date(torneo.fecha_fin);

                        let estadoBadge;
                        if (hoy < fechaInicio) {
                          estadoBadge = (
                            <span style={{
                              display: 'inline-block',
                              backgroundColor: '#17a2b8',
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              Próximo
                            </span>
                          );
                        } else if (hoy > fechaFin) {
                          estadoBadge = (
                            <span style={{
                              display: 'inline-block',
                              backgroundColor: '#6c757d',
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              Finalizado
                            </span>
                          );
                        } else {
                          estadoBadge = (
                            <span style={{
                              display: 'inline-block',
                              backgroundColor: '#28a745',
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              Activo
                            </span>
                          );
                        }

                        return (
                          <div key={torneo.id} className="col-12 col-sm-6 mb-3">
                            <div className="card shadow-sm h-100" style={torneo.tipo === 'summer' ? { backgroundColor: '#fffbea', borderColor: '#f0d060' } : torneo.tipo === 'custom' ? { backgroundColor: '#eef6ff', borderColor: '#90c0f0' } : {}}>
                              <div className="card-body p-3 d-flex flex-column">
                                {/* Header del torneo */}
                                <div className="d-flex align-items-start mb-3">
                                  <div className="tournament-icon me-3 bg-warning text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '45px', height: '45px', fontSize: '20px', fontWeight: 'bold', flexShrink: 0}}>
                                    <FaTrophy />
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                      <h6 className="card-title mb-0 fw-bold">
                                        {torneo.nombre}
                                        <span className="text-muted fw-normal" style={{ fontSize: '12px', marginLeft: '6px' }}>
                                          {torneo.tipo === 'summer' ? '☀️' : torneo.tipo === 'custom' ? '⚙️' : '🏆'}
                                        </span>
                                      </h6>
                                      {/* Badge de estado */}
                                      {estadoBadge}
                                    </div>
                                    <p className="card-text text-muted small mb-0">
                                      {torneo.organizacion?.nombre || 'Sin organización'}
                                    </p>
                                  </div>
                                </div>

                                {/* Información del torneo */}
                                <div className="mb-3">
                                  <div className="small text-muted">
                                    <div className="mb-1">
                                      <strong>📅 Temporada:</strong> {torneo.temporadaInfo?.nombre || 'Sin temporada'}
                                    </div>
                                    <div className="mb-1">
                                      <strong>📆 Fechas:</strong> {formatDate(torneo.fecha_inicio)} - {formatDate(torneo.fecha_fin)}
                                    </div>
                                    <div className="mb-1">
                                      <strong>🏟️ Jornadas:</strong> {torneo.estadisticas?.jornadasDisputadas || 0} / {torneo.estadisticas?.totalJornadas || 0}
                                    </div>
                                    <div className="mb-1">
                                      <strong>📍 Sedes:</strong> {torneo.estadisticas?.sedesActivas || 0}
                                    </div>
                                    <div className="mb-1">
                                      <strong>👥 Inscripciones:</strong> {torneo.estadisticas?.inscripcionesActivas || 0}
                                    </div>
                                  </div>
                                </div>

                                {/* Botón de ver futbolistas si hay inscripciones */}
                                {(torneo.estadisticas?.inscripcionesActivas || 0) > 0 && (
                                  <>
                                    <div className="mb-2">
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => handleViewFutbolistas(torneo)}
                                        className="w-100 d-flex align-items-center justify-content-center"
                                        style={{ fontSize: '12px', padding: '6px 8px' }}
                                      >
                                        <FaUsers className="me-1" size={12} />
                                        Ver Futbolistas Inscritos
                                      </Button>
                                    </div>
                                    <div className="mb-2">
                                      <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => handleExportExcelSeguimiento(torneo)}
                                        className="w-100 d-flex align-items-center justify-content-center"
                                        style={{ fontSize: '12px', padding: '6px 8px' }}
                                        disabled={loading}
                                      >
                                        <FaFileExcel className="me-1" size={12} />
                                        Excel Seguimiento
                                      </Button>
                                    </div>
                                    <div className="mb-2">
                                      <Button
                                        variant="warning"
                                        size="sm"
                                        onClick={() => navigate(`/admin/tournaments/${torneo.id}/propoints`)}
                                        className="w-100 d-flex align-items-center justify-content-center"
                                        style={{ fontSize: '12px', padding: '6px 8px' }}
                                      >
                                        <FaTrophy className="me-1" size={12} />
                                        ProPoints
                                      </Button>
                                    </div>
                                  </>
                                )}

                                {/* Botones de gestión del torneo */}
                                <div className="mb-2 d-flex flex-wrap gap-1">
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    onClick={() => navigate(`/admin/equipaciones?torneoId=${torneo.id}&torneoNombre=${encodeURIComponent(torneo.nombre)}`)}
                                    className="flex-fill d-flex align-items-center justify-content-center"
                                    style={{ fontSize: '11px', padding: '5px 6px' }}
                                  >
                                    <FaTshirt className="me-1" size={11} />
                                    Equipaciones
                                  </Button>
                                  <Button
                                    variant="outline-warning"
                                    size="sm"
                                    onClick={() => navigate(`/admin/inscripciones?torneoId=${torneo.id}&torneoNombre=${encodeURIComponent(torneo.nombre)}`)}
                                    className="flex-fill d-flex align-items-center justify-content-center"
                                    style={{ fontSize: '11px', padding: '5px 6px' }}
                                  >
                                    <FaUserPlus className="me-1" size={11} />
                                    Inscripción
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => navigate(`/admin/consumir-jornada?torneoId=${torneo.id}&torneoNombre=${encodeURIComponent(torneo.nombre)}`)}
                                    className="flex-fill d-flex align-items-center justify-content-center"
                                    style={{ fontSize: '11px', padding: '5px 6px' }}
                                  >
                                    <FaExclamationTriangle className="me-1" size={11} />
                                    Consumir
                                  </Button>
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    onClick={() => navigate(`/admin/cambio-sede?torneoId=${torneo.id}&torneoNombre=${encodeURIComponent(torneo.nombre)}`)}
                                    className="flex-fill d-flex align-items-center justify-content-center"
                                    style={{ fontSize: '11px', padding: '5px 6px' }}
                                  >
                                    <FaExchangeAlt className="me-1" size={11} />
                                    Cambio Sede
                                  </Button>
                                </div>

                                {/* Botones de acción - Solo iconos */}
                                <div className="d-flex justify-content-end gap-1 pt-2 border-top mt-auto">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleViewTorneo(torneo)}
                                    className="p-1 d-flex align-items-center justify-content-center"
                                    style={{width: '32px', height: '32px'}}
                                    title="Ver detalles"
                                  >
                                    <FaEye size={14} />
                                  </Button>
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => handleOpenCalendar(torneo)}
                                    className="p-1 d-flex align-items-center justify-content-center"
                                    style={{width: '32px', height: '32px'}}
                                    title="Calendario de jornadas"
                                  >
                                    <FaCalendarAlt size={14} />
                                  </Button>
                                  <Button
                                    variant="outline-warning"
                                    size="sm"
                                    onClick={() => handleEditTorneo(torneo)}
                                    className="p-1 d-flex align-items-center justify-content-center"
                                    style={{width: '32px', height: '32px'}}
                                    title="Editar torneo"
                                  >
                                    <FaEdit size={14} />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteTorneo(torneo)}
                                    className="p-1 d-flex align-items-center justify-content-center"
                                    style={{width: '32px', height: '32px'}}
                                    title="Eliminar torneo"
                                  >
                                    <FaTrash size={14} />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modal de Calendario de Jornadas */}
      {showCalendarModal && calendarTorneo && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowCalendarModal(false); }}>
          <div style={{ backgroundColor: '#fff', borderRadius: window.innerWidth <= 768 ? '0' : '0.5rem', width: window.innerWidth <= 768 ? '100vw' : '90vw', maxWidth: window.innerWidth <= 768 ? '100vw' : '900px', height: window.innerWidth <= 768 ? '100vh' : 'auto', maxHeight: window.innerWidth <= 768 ? '100vh' : '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}
            onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #dee2e6', flexShrink: 0 }}>
              <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 600, color: '#333' }}>
                <FaCalendarAlt style={{ marginRight: '0.5rem', color: '#28a745' }} />
                Calendario — {calendarTorneo.nombre}
              </h2>
              <button onClick={() => setShowCalendarModal(false)} style={{ position: 'absolute', right: '1rem', top: '1rem', background: 'transparent', border: 'none', fontSize: '1.5rem', fontWeight: 'bold', color: '#000', cursor: 'pointer', padding: '0.5rem', lineHeight: 1 }}>×</button>
            </div>

            {/* Tabs de sedes */}
            {calendarSedes.length > 0 ? (
              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', borderBottom: '2px solid #dee2e6', padding: '0 1.5rem', flexShrink: 0 }}>
                  {calendarSedes.map(sede => (
                    <div key={sede.sede_en_torneo_id}
                      onClick={() => handleSelectSedeTab(sede)}
                      style={{
                        padding: '10px 20px', cursor: 'pointer', fontWeight: calendarSedeActiva?.sede_en_torneo_id === sede.sede_en_torneo_id ? '700' : '400',
                        color: calendarSedeActiva?.sede_en_torneo_id === sede.sede_en_torneo_id ? '#28a745' : '#666',
                        borderBottom: calendarSedeActiva?.sede_en_torneo_id === sede.sede_en_torneo_id ? '3px solid #28a745' : '3px solid transparent',
                        marginBottom: '-2px', fontSize: '14px', transition: 'all 0.2s',
                      }}>
                      📍 {sede.sede_nombre}{sede.sede_poblacion ? ` (${sede.sede_poblacion})` : ''}
                    </div>
                  ))}
                </div>

                {/* Contenido de la sede activa */}
                <div style={{ padding: '1.5rem', flex: 1, overflowY: 'auto' }}>
                  {calendarLoading ? (
                    <div className="text-center py-4"><Spinner animation="border" /></div>
                  ) : (
                    <>
                      {/* Formulario para añadir jornada */}
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '20px', flexWrap: 'wrap', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                        <div style={{ flex: '1', minWidth: '150px' }}>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: '#666', display: 'block', marginBottom: '4px' }}>Fecha</label>
                          <Form.Control type="date" value={newJornada.fecha} onChange={(e) => setNewJornada(prev => ({ ...prev, fecha: e.target.value }))}
                            style={{ fontSize: '14px', minHeight: '38px' }} />
                        </div>
                        <div style={{ flex: '0 0 120px' }}>
                          <label style={{ fontSize: '12px', fontWeight: '600', color: '#666', display: 'block', marginBottom: '4px' }}>Hora</label>
                          <Form.Control type="time" value={newJornada.hora} onChange={(e) => setNewJornada(prev => ({ ...prev, hora: e.target.value }))}
                            style={{ fontSize: '14px', minHeight: '38px' }} />
                        </div>
                        <Button variant="success" onClick={handleAddJornada} disabled={!newJornada.fecha}
                          style={{ minHeight: '38px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <FaPlus size={12} /> Añadir Jornada
                        </Button>
                      </div>

                      {/* Lista de jornadas */}
                      {calendarJornadas.length === 0 ? (
                        <p className="text-muted text-center py-4">No hay jornadas creadas para esta sede.</p>
                      ) : (
                        <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead>
                              <tr style={{ backgroundColor: '#f8f9fa' }}>
                                <th style={{ padding: '10px 15px', textAlign: 'left', borderBottom: '2px solid #dee2e6', width: '60px' }}>#</th>
                                <th style={{ padding: '10px 15px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Fecha</th>
                                <th style={{ padding: '10px 15px', textAlign: 'left', borderBottom: '2px solid #dee2e6', width: '100px' }}>Hora</th>
                                <th style={{ padding: '10px 15px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Límite Inscripción</th>
                                <th style={{ padding: '10px 15px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '80px' }}>Estado</th>
                                <th style={{ padding: '10px 15px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '60px' }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {calendarJornadas.map(j => (
                                <tr key={j.id} style={{ borderBottom: '1px solid #eee' }}>
                                  <td style={{ padding: '10px 15px', fontWeight: '700', color: '#061156' }}>J{j.numero}</td>
                                  <td style={{ padding: '10px 15px' }}>
                                    <Form.Control type="date" value={j.fecha?.substring(0, 10) || ''} size="sm"
                                      onChange={(e) => handleUpdateJornada(j.id, 'fecha', e.target.value)}
                                      style={{ fontSize: '13px', width: 'auto', display: 'inline-block' }} />
                                  </td>
                                  <td style={{ padding: '10px 15px' }}>
                                    <Form.Control type="time" value={j.hora?.substring(0, 5) || ''} size="sm"
                                      onChange={(e) => handleUpdateJornada(j.id, 'hora', e.target.value)}
                                      style={{ fontSize: '13px', width: '100px' }} />
                                  </td>
                                  <td style={{ padding: '10px 15px', color: '#666', fontSize: '13px' }}>
                                    {j.fecha_limite_inscripcion ? new Date(j.fecha_limite_inscripcion).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                                  </td>
                                  <td style={{ padding: '10px 15px', textAlign: 'center' }}>
                                    <span style={{
                                      padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                                      backgroundColor: j.disputada ? '#d4edda' : '#fff3cd',
                                      color: j.disputada ? '#155724' : '#856404'
                                    }}>
                                      {j.disputada ? 'Disputada' : 'Pendiente'}
                                    </span>
                                  </td>
                                  <td style={{ padding: '10px 15px', textAlign: 'center' }}>
                                    {!j.disputada && (
                                      <FaTrash size={13} style={{ cursor: 'pointer', color: '#dc3545' }}
                                        title="Eliminar jornada" onClick={() => handleDeleteJornada(j.id)} />
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <p className="text-muted">Este torneo no tiene sedes asociadas. Añade sedes primero desde la edición del torneo.</p>
              </div>
            )}

            {/* Footer */}
            <div style={{ padding: '1rem', borderTop: '1px solid #dee2e6', flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setShowCalendarModal(false)}>Cerrar</Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Creación de Torneo */}
      {showCreateModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowCreateModal(false); }}>
          <div style={{ backgroundColor: '#fff', borderRadius: window.innerWidth <= 768 ? '0' : '0.5rem', width: window.innerWidth <= 768 ? '100vw' : '90vw', maxWidth: window.innerWidth <= 768 ? '100vw' : '800px', height: window.innerWidth <= 768 ? '100vh' : 'auto', maxHeight: window.innerWidth <= 768 ? '100vh' : '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}
            onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #dee2e6', backgroundColor: '#fff', position: 'relative', flexShrink: 0 }}>
              <h2 style={{ margin: 0, fontSize: window.innerWidth <= 768 ? '1.2rem' : '1.4rem', fontWeight: 600, color: '#333' }}>
                <FaPlus style={{ marginRight: '0.5rem', color: '#007bff' }} /> Crear Nuevo Torneo
              </h2>
              <button onClick={() => setShowCreateModal(false)} style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', fontSize: '1.5rem', fontWeight: 'bold', color: '#000', cursor: 'pointer', padding: '0.5rem', lineHeight: 1 }}>×</button>
            </div>

            <Form onSubmit={handleSaveCreateTorneo} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <div style={{ padding: '1.5rem', backgroundColor: '#fff', flex: 1, overflowY: 'auto' }}>
                <Row className="mb-3">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label className="fw-bold">Nombre del Torneo</Form.Label>
                      <Form.Control type="text" name="nombre" value={createForm.nombre} onChange={handleCreateFormChange} required placeholder="Ej: Liga ProKids Summer 2026" style={{ fontSize: window.innerWidth <= 768 ? '16px' : '14px', minHeight: window.innerWidth <= 768 ? '44px' : 'auto' }} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="fw-bold">Tipo</Form.Label>
                      <Form.Select name="tipo" value={createForm.tipo} onChange={handleCreateFormChange} required style={{ fontSize: window.innerWidth <= 768 ? '16px' : '14px', minHeight: window.innerWidth <= 768 ? '44px' : 'auto' }}>
                        <option value="normal">Normal</option>
                        <option value="summer">Summer</option>
                        <option value="custom">Custom</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold">Fecha Inicio</Form.Label>
                      <Form.Control type="date" name="fecha_inicio" value={createForm.fecha_inicio} onChange={handleCreateFormChange} required style={{ fontSize: window.innerWidth <= 768 ? '16px' : '14px', minHeight: window.innerWidth <= 768 ? '44px' : 'auto' }} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold">Fecha Fin</Form.Label>
                      <Form.Control type="date" name="fecha_fin" value={createForm.fecha_fin} onChange={handleCreateFormChange} required style={{ fontSize: window.innerWidth <= 768 ? '16px' : '14px', minHeight: window.innerWidth <= 768 ? '44px' : 'auto' }} />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold">Nº Jornadas</Form.Label>
                      <Form.Control type="number" name="jornadas" value={createForm.jornadas} onChange={handleCreateFormChange} required min="1" placeholder="Número total de jornadas" style={{ fontSize: window.innerWidth <= 768 ? '16px' : '14px', minHeight: window.innerWidth <= 768 ? '44px' : 'auto' }} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold">Temporada</Form.Label>
                      <Form.Select name="temporada" value={createForm.temporada} onChange={handleCreateFormChange} required style={{ fontSize: window.innerWidth <= 768 ? '16px' : '14px', minHeight: window.innerWidth <= 768 ? '44px' : 'auto' }}>
                        <option value="">Seleccionar temporada...</option>
                        {temporadas.map(t => (
                          <option key={t.id} value={t.id}>{t.nombre}{t.activa ? ' (activa)' : ''}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                {/* Selector de sedes */}
                <div className="mb-3">
                  <Form.Label className="fw-bold">Sedes asociadas</Form.Label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                    {sedesDisponibles.map(sede => {
                      const seleccionada = sedesSeleccionadas.find(s => s.id === sede.id);
                      return (
                        <div key={sede.id} onClick={() => handleToggleSede(sede)}
                          style={{
                            padding: '8px 16px', borderRadius: '20px', cursor: 'pointer',
                            border: seleccionada ? '2px solid #0d6efd' : '2px solid #dee2e6',
                            backgroundColor: seleccionada ? '#e7f1ff' : '#f8f9fa',
                            color: seleccionada ? '#0d6efd' : '#666',
                            fontWeight: seleccionada ? '600' : '400',
                            fontSize: '14px', transition: 'all 0.2s ease',
                            userSelect: 'none',
                            display: 'flex', alignItems: 'center', gap: '8px'
                          }}>
                          <span>{seleccionada ? '✓ ' : ''}{sede.nombre}{sede.poblacion ? ` (${sede.poblacion})` : ''}</span>
                          {seleccionada && (
                            <Form.Check 
                              type="switch"
                              id={`switch-eq-${sede.id}`}
                              label={<span style={{fontSize: '11px', color: '#666', fontWeight: 'bold'}}>Forzar Eq. Nueva</span>}
                              checked={seleccionada.equipacion_nueva || false}
                              onChange={(e) => handleToggleEquipacionNueva(e, sede.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="ms-2"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {sedesSeleccionadas.length === 0 && (
                    <small className="text-muted">Haz clic en las sedes que quieras asociar al torneo</small>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div style={{ padding: '1rem', borderTop: '1px solid #dee2e6', backgroundColor: '#fff', flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={isSubmitting} style={{ minWidth: '80px', fontSize: '14px', padding: '0.5rem 1rem', border: '1px solid #6c757d', backgroundColor: '#6c757d', color: '#fff', borderRadius: '0.25rem', cursor: 'pointer' }}>
                  Cancelar
                </Button>
                <Button type="submit" variant="primary" disabled={isSubmitting} style={{ minWidth: '80px', fontSize: '14px', padding: '0.5rem 1rem', border: '1px solid #0d6efd', backgroundColor: '#0d6efd', color: '#fff', borderRadius: '0.25rem', cursor: 'pointer' }}>
                  {isSubmitting ? <><Spinner animation="border" size="sm" /> Creando...</> : 'Crear Torneo'}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}

      {/* Modal de Vista de Torneo - Custom Modal */}
      {showViewModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 99999,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: window.innerWidth <= 768 ? '0.5rem' : '1rem'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowViewModal(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: window.innerWidth <= 768 ? '0' : '0.5rem',
              width: window.innerWidth <= 768 ? '100vw' : '90vw',
              maxWidth: window.innerWidth <= 768 ? '100vw' : '800px',
              height: window.innerWidth <= 768 ? '100vh' : 'auto',
              maxHeight: window.innerWidth <= 768 ? '100vh' : '90vh',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header del Modal */}
            <div
              style={{
                padding: '1.5rem',
                borderBottom: '1px solid #dee2e6',
                backgroundColor: '#fff',
                position: 'relative',
                flexShrink: 0
              }}
            >
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#333',
                margin: 0,
                paddingRight: '2rem'
              }}>
                <FaTrophy style={{ marginRight: '0.5rem' }} />
                Detalles del Torneo
              </h2>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  position: 'absolute',
                  right: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: '#000',
                  opacity: 0.5,
                  cursor: 'pointer',
                  padding: 0,
                  margin: 0,
                  width: 'auto',
                  height: 'auto',
                  lineHeight: 1
                }}
                onMouseEnter={(e) => e.target.style.opacity = '0.75'}
                onMouseLeave={(e) => e.target.style.opacity = '0.5'}
              >
                ×
              </button>
            </div>

            {/* Body del Modal */}
            <div
              style={{
                padding: '1.5rem',
                backgroundColor: '#fff',
                flex: 1,
                overflowY: 'auto'
              }}
            >
              {selectedTorneo && (
                <div>
                  <Row className="mb-3">
                    <Col md={6}>
                      <strong>Nombre:</strong>
                      <p>{selectedTorneo.nombre}</p>
                    </Col>
                    <Col md={6}>
                      <strong>Estado:</strong>
                      <p>
                        <span className={`badge ${
                          (() => {
                            const hoy = new Date();
                            const fechaInicio = new Date(selectedTorneo.fecha_inicio);
                            const fechaFin = new Date(selectedTorneo.fecha_fin);

                            if (hoy < fechaInicio) {
                              return 'bg-primary';
                            } else if (hoy > fechaFin) {
                              return 'bg-secondary';
                            } else {
                              return 'bg-success';
                            }
                          })()
                        }`}>
                          {(() => {
                            const hoy = new Date();
                            const fechaInicio = new Date(selectedTorneo.fecha_inicio);
                            const fechaFin = new Date(selectedTorneo.fecha_fin);

                            if (hoy < fechaInicio) {
                              return 'Próximo';
                            } else if (hoy > fechaFin) {
                              return 'Finalizado';
                            } else {
                              return 'Activo';
                            }
                          })()}
                        </span>
                      </p>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <strong>Fecha de Inicio:</strong>
                      <p>{formatDate(selectedTorneo.fecha_inicio)}</p>
                    </Col>
                    <Col md={6}>
                      <strong>Fecha de Fin:</strong>
                      <p>{formatDate(selectedTorneo.fecha_fin)}</p>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={4}>
                      <strong>Jornadas:</strong>
                      <p>{selectedTorneo.estadisticas?.jornadasDisputadas || 0} / {selectedTorneo.estadisticas?.totalJornadas || 0}</p>
                    </Col>
                    <Col md={4}>
                      <strong>Sedes:</strong>
                      <p>{selectedTorneo.estadisticas?.sedesActivas || 0}</p>
                    </Col>
                    <Col md={4}>
                      <strong>Inscripciones:</strong>
                      <p>{selectedTorneo.estadisticas?.inscripcionesActivas || 0}</p>
                    </Col>
                  </Row>

                  {selectedTorneo.sedesEnTorneo && selectedTorneo.sedesEnTorneo.length > 0 && (
                    <div className="mb-3">
                      <strong>Sedes del Torneo:</strong>
                      <ul className="list-unstyled mt-2">
                        {selectedTorneo.sedesEnTorneo.map((sedeEnTorneo) => (
                          <li key={sedeEnTorneo.id} className="mb-2">
                            <Card className="border-0 bg-light">
                              <Card.Body className="py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <strong>{sedeEnTorneo.sede?.nombre}</strong>
                                    <br />
                                    <small className="text-muted">
                                      {sedeEnTorneo.sede?.direccion}, {sedeEnTorneo.sede?.poblacion}
                                    </small>
                                  </div>
                                  <div className="text-end">
                                    <small className="text-muted">
                                      Jornadas: {sedeEnTorneo.jornadas?.length || 0}
                                    </small>
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer del Modal */}
            <div
              style={{
                padding: '1rem',
                borderTop: '1px solid #dee2e6',
                backgroundColor: '#fff',
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <Button
                variant="secondary"
                onClick={() => setShowViewModal(false)}
                style={{
                  minWidth: '80px',
                  fontSize: '14px',
                  padding: '0.5rem 1rem',
                  border: '1px solid #6c757d',
                  backgroundColor: '#6c757d',
                  color: '#fff',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición de Torneo - Custom Modal */}
      {showEditModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 99999,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: window.innerWidth <= 768 ? '0.5rem' : '1rem'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowEditModal(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: window.innerWidth <= 768 ? '0' : '0.5rem',
              width: window.innerWidth <= 768 ? '100vw' : '90vw',
              maxWidth: window.innerWidth <= 768 ? '100vw' : '800px',
              height: window.innerWidth <= 768 ? '100vh' : 'auto',
              maxHeight: window.innerWidth <= 768 ? '100vh' : '90vh',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header del Modal */}
            <div
              style={{
                padding: '1.5rem',
                borderBottom: '1px solid #dee2e6',
                backgroundColor: '#fff',
                position: 'relative',
                flexShrink: 0
              }}
            >
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#333',
                margin: 0,
                paddingRight: '2rem'
              }}>
                <FaEdit style={{ marginRight: '0.5rem' }} />
                Editar Torneo
              </h2>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  position: 'absolute',
                  right: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: '#000',
                  opacity: 0.5,
                  cursor: 'pointer',
                  padding: 0,
                  margin: 0,
                  width: 'auto',
                  height: 'auto',
                  lineHeight: 1
                }}
                onMouseEnter={(e) => e.target.style.opacity = '0.75'}
                onMouseLeave={(e) => e.target.style.opacity = '0.5'}
              >
                ×
              </button>
            </div>

            <Form onSubmit={handleSaveEditTorneo} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              {/* Body del Modal */}
              <div
                style={{
                  padding: '1.5rem',
                  backgroundColor: '#fff',
                  flex: 1,
                  overflowY: 'auto'
                }}
              >
                {selectedTorneo && (
                  <div>
                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Nombre del Torneo</Form.Label>
                          <Form.Control
                            type="text"
                            name="nombre"
                            value={editForm.nombre}
                            onChange={handleEditFormChange}
                            required
                            placeholder="Ingresa el nombre del torneo"
                            style={{
                              fontSize: window.innerWidth <= 768 ? '16px' : '14px',
                              minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Fecha de Inicio</Form.Label>
                          <Form.Control
                            type="date"
                            name="fecha_inicio"
                            value={editForm.fecha_inicio}
                            onChange={handleEditFormChange}
                            required
                            style={{
                              fontSize: window.innerWidth <= 768 ? '16px' : '14px',
                              minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Fecha de Fin</Form.Label>
                          <Form.Control
                            type="date"
                            name="fecha_fin"
                            value={editForm.fecha_fin}
                            onChange={handleEditFormChange}
                            required
                            style={{
                              fontSize: window.innerWidth <= 768 ? '16px' : '14px',
                              minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Número de Jornadas</Form.Label>
                          <Form.Control
                            type="number"
                            name="jornadas"
                            value={editForm.jornadas}
                            onChange={handleEditFormChange}
                            required
                            min="1"
                            placeholder="Número total de jornadas"
                            style={{
                              fontSize: window.innerWidth <= 768 ? '16px' : '14px',
                              minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="alert alert-info">
                      <strong>Nota:</strong> Los cambios en las fechas y número de jornadas pueden afectar las jornadas ya programadas.
                    </div>

                    {/* Gestión de sedes */}
                    <div className="mb-3 mt-3">
                      <Form.Label className="fw-bold">Sedes asociadas</Form.Label>
                      {/* Sedes actualmente asociadas */}
                      {sedesDelTorneo.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                          {sedesDelTorneo.map(st => (
                            <div key={st.sede_en_torneo_id} style={{
                              padding: '8px 16px', borderRadius: '20px',
                              border: '2px solid #28a745', backgroundColor: '#e8f5e9',
                              color: '#28a745', fontWeight: '600', fontSize: '14px',
                              display: 'flex', alignItems: 'center', gap: '8px'
                            }}>
                                ✓ {st.sede_nombre}{st.sede_poblacion ? ` (${st.sede_poblacion})` : ''}
                                <Form.Check 
                                  type="switch"
                                  id={`edit-switch-eq-${st.sede_en_torneo_id}`}
                                  label={<span style={{fontSize: '11px', color: '#155724', fontWeight: 'bold'}}>Forzar Eq. Nueva</span>}
                                  checked={st.equipacion_nueva || false}
                                  onChange={(e) => handleToggleEquipacionNuevaEdit(e, st.sede_en_torneo_id, st.equipacion_nueva)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="ms-3"
                                />
                                <span onClick={() => handleRemoveSedeFromTorneo({ id: st.sede_en_torneo_id })}
                                  style={{ cursor: 'pointer', color: '#dc3545', fontWeight: 'bold', fontSize: '16px', marginLeft: 'auto' }}
                                  title="Quitar sede">×</span>
                              </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted mb-2" style={{ fontSize: '14px' }}>No hay sedes asociadas.</p>
                      )}
                      {/* Sedes disponibles para añadir */}
                      {sedesDisponibles.filter(s => !sedesDelTorneo.find(st => st.sede_id === s.id)).length > 0 && (
                        <>
                          <small className="text-muted d-block mb-2">Haz clic para añadir:</small>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {sedesDisponibles
                              .filter(s => !sedesDelTorneo.find(st => st.sede_id === s.id))
                              .map(sede => (
                                <div key={sede.id} onClick={() => handleAddSedeToTorneo(sede)}
                                  style={{
                                    padding: '8px 16px', borderRadius: '20px', cursor: 'pointer',
                                    border: '2px solid #dee2e6', backgroundColor: '#f8f9fa',
                                    color: '#666', fontSize: '14px', transition: 'all 0.2s ease',
                                    userSelect: 'none',
                                  }}>
                                  + {sede.nombre}{sede.poblacion ? ` (${sede.poblacion})` : ''}
                                </div>
                              ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer del Modal */}
              <div
                style={{
                  padding: '1rem',
                  borderTop: '1px solid #dee2e6',
                  backgroundColor: '#fff',
                  flexShrink: 0,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '0.5rem'
                }}
              >
                <Button
                  variant="secondary"
                  onClick={() => setShowEditModal(false)}
                  disabled={isSubmitting}
                  style={{
                    minWidth: '80px',
                    fontSize: '14px',
                    padding: '0.5rem 1rem',
                    border: '1px solid #6c757d',
                    backgroundColor: '#6c757d',
                    color: '#fff',
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    minWidth: '120px',
                    fontSize: '14px',
                    padding: '0.5rem 1rem',
                    border: '1px solid #0d6efd',
                    backgroundColor: '#0d6efd',
                    color: '#fff',
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <FaEdit className="me-2" />
                      Guardar Cambios
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación - Custom Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 99999,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: window.innerWidth <= 768 ? '0.5rem' : '1rem'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeleteModal(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: window.innerWidth <= 768 ? '0' : '0.5rem',
              width: window.innerWidth <= 768 ? '100vw' : '90vw',
              maxWidth: window.innerWidth <= 768 ? '100vw' : '500px',
              height: window.innerWidth <= 768 ? '100vh' : 'auto',
              maxHeight: window.innerWidth <= 768 ? '100vh' : '90vh',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header del Modal */}
            <div
              style={{
                padding: '1.5rem',
                borderBottom: '1px solid #dee2e6',
                backgroundColor: '#fff',
                position: 'relative',
                flexShrink: 0
              }}
            >
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#dc3545',
                margin: 0,
                paddingRight: '2rem'
              }}>
                <FaTrash style={{ marginRight: '0.5rem' }} />
                Confirmar Eliminación
              </h2>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  position: 'absolute',
                  right: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: '#000',
                  opacity: 0.5,
                  cursor: 'pointer',
                  padding: 0,
                  margin: 0,
                  width: 'auto',
                  height: 'auto',
                  lineHeight: 1
                }}
                onMouseEnter={(e) => e.target.style.opacity = '0.75'}
                onMouseLeave={(e) => e.target.style.opacity = '0.5'}
              >
                ×
              </button>
            </div>

            {/* Body del Modal */}
            <div
              style={{
                padding: '1.5rem',
                backgroundColor: '#fff',
                flex: 1,
                overflowY: 'auto'
              }}
            >
              {selectedTorneo && (
                <div>
                  <p>¿Estás seguro de que quieres eliminar el torneo <strong>&ldquo;{selectedTorneo.nombre}&rdquo;</strong>?</p>
                  <div className="alert alert-warning">
                    <strong>¡Atención!</strong> Esta acción no se puede deshacer. Se eliminarán:
                    <ul className="mb-0 mt-2">
                      <li>Todas las jornadas programadas</li>
                      <li>Las inscripciones de futbolistas</li>
                      <li>Los datos estadísticos del torneo</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Footer del Modal */}
            <div
              style={{
                padding: '1rem',
                borderTop: '1px solid #dee2e6',
                backgroundColor: '#fff',
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '0.5rem'
              }}
            >
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={isSubmitting}
                style={{
                  minWidth: '80px',
                  fontSize: '14px',
                  padding: '0.5rem 1rem',
                  border: '1px solid #6c757d',
                  backgroundColor: '#6c757d',
                  color: '#fff',
                  borderRadius: '0.25rem',
                  cursor: 'pointer',
                  minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={confirmDeleteTorneo}
                disabled={isSubmitting}
                style={{
                  minWidth: '140px',
                  fontSize: '14px',
                  padding: '0.5rem 1rem',
                  border: '1px solid #dc3545',
                  backgroundColor: '#dc3545',
                  color: '#fff',
                  borderRadius: '0.25rem',
                  cursor: 'pointer',
                  minHeight: window.innerWidth <= 768 ? '44px' : 'auto'
                }}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <FaTrash className="me-2" />
                    Eliminar Torneo
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default TournamentManagement;
